import type { RoleCode } from './enums';

/**
 * Role → permission map. The server enforces it on every route; the client
 * uses the same map only to hide controls the user cannot use.
 */
export const PERMISSIONS = {
  /** Read any master data, transactions, and reports. */
  'data.read': ['ADMIN', 'ACCOUNTANT', 'VIEWER'],
  /** Apps, platforms, ad/revenue accounts, revenue sources, categories, currencies, GST rates. */
  'masters.write': ['ADMIN'],
  'gstAccounts.write': ['ADMIN', 'ACCOUNTANT'],
  'currencyRates.write': ['ADMIN', 'ACCOUNTANT'],
  /** Re-apply changed exchange rates to historical revenue (Phase 3). */
  'fx.recalculate': ['ADMIN'],
  /** Override the monthly exchange rate on an individual revenue entry. */
  'fx.override': ['ADMIN'],
  /** Marketing expenses, revenue entries, other expenses, morning entry. */
  'transactions.write': ['ADMIN', 'ACCOUNTANT'],
  /** ITC status changes and GST claim periods. */
  'gst.manage': ['ADMIN', 'ACCOUNTANT'],
  /** Reopen a submitted / claimed / closed GST claim period. */
  'gst.reopen': ['ADMIN'],
  'users.manage': ['ADMIN'],
  'settings.write': ['ADMIN'],
  'audit.read': ['ADMIN', 'ACCOUNTANT'],
} as const satisfies Record<string, readonly RoleCode[]>;

export type Permission = keyof typeof PERMISSIONS;

export function can(role: RoleCode | undefined | null, permission: Permission): boolean {
  if (!role) return false;
  return (PERMISSIONS[permission] as readonly RoleCode[]).includes(role);
}

export function permissionsFor(role: RoleCode): Permission[] {
  return (Object.keys(PERMISSIONS) as Permission[]).filter((p) => can(role, p));
}
